import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { isNumber } from "lodash";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import {
  ObjectPropertiesWrapper,
  ObjectWrapper,
  Checkboxes,
  Comments,
  DescriptionWrapper,
  Divider,
  ObjectSubscribersPanel,
  Attachments,
} from "~/components/ModalObjects";
import { useNoteVersion } from "~/repository";
import { editNote, revertNoteToOriginal, revertNoteToModification } from "~/store/actions/actions.notes";
import { getNoteById } from "~/store/selectors/selector.notes";
import { getProjectMembersById } from "~/store/selectors/selector.projects";
import { isLoggedUserClientSelector } from "~/store/selectors/selector.users";
import { Header } from "./Header";
import { Metadata } from "./Metadata";
import { NotebookSelectContainer } from "./NotebookSelectContainer";
import FormContainer from "./NoteFormContainer";
import { useVersionContext } from "./VersionContext";
import { VersionDescriptionLoader } from "./VersionDescriptionLoader";
import { VersionHeader } from "./VersionHeader";
import { VersionNameLoader } from "./VersionNameLoader";
import { Versions } from "./Versions";
import NewCommentsNotification from "../comments/NewCommentsNotification";
import ObjectHistory from "../ObjectHistory/ObjectHistory";
import RemindersContainer from "../Reminders/RemindersContainer";

export const Note = ({ id, projectId, showNavigation, edit, setEdit, onCloseForm, shouldOverflow }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const can = useSecurity();

  const note = useSelector((state) => getNoteById(state, id));
  const projectMembersIds = useSelector((state) => getProjectMembersById(state, projectId));
  const canEdit = can(["edit"], note);
  const isUserClientSelector = useSelector(isLoggedUserClientSelector);

  const { versionId, modificationId, setVersionId, setModificationId } = useVersionContext();
  const active = versionId > 0;

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries(["note-navigation", projectId, id]);
  }, [id, projectId, queryClient]);

  const onChange = useCallback(
    (attributes) => {
      return dispatch(editNote(projectId, id, attributes)).then(() => {
        invalidateQuery();
      });
    },
    [dispatch, id, invalidateQuery, projectId]
  );

  const handleEdit = useCallback(() => {
    setEdit(true);
  }, [setEdit]);

  const [revertLoading, setRevertLoading] = useState(false);

  const handleRevert = useCallback(
    (modificationId) => {
      setRevertLoading(true);
      dispatch(
        modificationId > 0
          ? revertNoteToModification(projectId, id, modificationId)
          : revertNoteToOriginal(projectId, id)
      ).then(() => {
        queryClient.invalidateQueries(["note-versions", projectId, id]);
        setRevertLoading(false);
      });
    },
    [dispatch, id, projectId, queryClient]
  );

  const handleOnClose = useCallback(() => {
    typeof setEdit === "function" && setEdit(false);
    typeof onCloseForm === "function" && onCloseForm();
  }, [onCloseForm, setEdit]);

  const exitVersions = useCallback(() => {
    setVersionId(0);
    setModificationId(null);
  }, [setModificationId, setVersionId]);

  const showVersions = isNumber(modificationId) && versionId > 0;

  const { data: version, isLoading } = useNoteVersion(projectId, id, modificationId, {
    enabled: Number.isInteger(modificationId),
  });

  const loading = isLoading || revertLoading;

  return (
    <>
      {!edit ? (
        <ObjectWrapper className={classNames({ "tw-bg-primary-200": active })}>
          <div
            className="tw-px-6 tw-pt-6"
            data-testid="note-sheet"
          >
            {!showVersions ? (
              <Header
                id={id}
                name={note?.name}
                hiddenFromClients={note?.is_hidden_from_clients}
                pinned={note?.is_pinned}
                createdById={note?.created_by_id}
                projectId={note?.project_id}
                showNavigation={showNavigation}
                onEdit={handleEdit}
                className="tw-mb-4 tw-relative"
                loading={loading}
              >
                {note ? (
                  <Metadata
                    projectId={note?.project_id}
                    createdByEmail={note?.created_by_email}
                    createdOn={note?.created_on}
                    createdById={note?.created_by_id}
                    createdByName={note?.created_by_name}
                  />
                ) : null}
                <NewCommentsNotification
                  parentType="Note"
                  parentId={id}
                />
              </Header>
            ) : (
              <>
                <VersionHeader
                  versionId={versionId}
                  onClick={exitVersions}
                />
                <VersionNameLoader
                  name={version?.name}
                  loading={isLoading}
                  className="tw-mb-2 tw-mt-1"
                />
                <Metadata
                  className="tw-mb-4"
                  projectId={note?.project_id}
                  createdByEmail={note?.created_by_email}
                  createdOn={note?.created_on}
                  createdById={note?.created_by_id}
                  createdByName={note?.created_by_name}
                />
              </>
            )}
            {!loading ? (
              <DescriptionWrapper
                parentId={id}
                parentType="Note"
                body={!showVersions ? note?.body_formatted : version?.body}
                onDoubleClick={!showVersions ? handleEdit : undefined}
                className={classNames("tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary", {
                  "tw-pb-4 ": note?.body_formatted,
                  "tw-border-none": showVersions,
                })}
              />
            ) : (
              <VersionDescriptionLoader />
            )}
            {!showVersions ? (
              <Attachments
                id={id}
                type="Note"
                className="tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary"
              />
            ) : null}
          </div>
          <ObjectPropertiesWrapper className={classNames({ "lg:tw-bg-page-paper-main": active })}>
            <NotebookSelectContainer
              projectId={projectId}
              selectedId={note?.notebook_id}
              createdById={note?.created_by_id}
              onChange={onChange}
              className="tw-mb-5 tw-max-w-[280px]"
            />
            <Checkboxes
              hiddenFromClients={note?.is_hidden_from_clients}
              pinned={note?.is_pinned}
              onChange={onChange}
              className="tw-ml-1"
              type="note"
            />
            <Divider className="tw-mt-4" />
            <ObjectSubscribersPanel
              parentId={id}
              hiddenFromClients={note?.is_hidden_from_clients}
              parentType="Note"
              canEdit={canEdit}
              members={projectMembersIds}
            />
            <Versions
              id={id}
              projectId={projectId}
              onRevert={handleRevert}
            />
            {!isUserClientSelector ? (
              <>
                <Divider className="tw-my-4" />
                <RemindersContainer
                  parent={note}
                  members={projectMembersIds}
                />
              </>
            ) : null}
          </ObjectPropertiesWrapper>
          {!showVersions && (
            <div className="tw-px-6">
              <Comments
                hiddenFromClients={note?.is_hidden_from_clients}
                parentId={id}
                parentType="Note"
                commentsCount={note?.comments_count}
                projectId={projectId}
                onAddComment={invalidateQuery}
              />
              <ObjectHistory
                objectId={id}
                objectType="Note"
              />
            </div>
          )}
        </ObjectWrapper>
      ) : (
        <FormContainer
          noteId={id}
          projectId={note?.project_id}
          notebookId={note?.notebook_id}
          submitButtonLabel={t("Save")}
          onClose={handleOnClose}
          className="tw-p-8"
          shouldOverflow={shouldOverflow}
        />
      )}
    </>
  );
};

Note.propTypes = {
  id: PropTypes.number,
  projectId: PropTypes.number,
  showNavigation: PropTypes.bool,
  edit: PropTypes.bool,
  setEdit: PropTypes.func,
  onCloseForm: PropTypes.func,
  shouldOverflow: PropTypes.bool,
};

Note.defaultProps = {
  edit: false,
  showNavigation: false,
};
